/**
 * @spec docs/superpowers/specs/2026-10-15-staking-ui-wallet-actions-design.md §4.3
 * @standard docs/standards/staking-ui-wallet-actions.md#signing
 *
 * The only code that asks a wallet for a signature. Both helpers take a PreparedWallet, so a flow
 * that skipped prepareWallet does not compile. Provider errors leave as WalletActionError with the
 * provider error as cause (wrap.ts); on any failure a transaction's lock is released.
 */
import { ethers } from 'ethers';
import { getChainDisplayName } from '@/lib/chain-registry';
import { PHENOM_WALLET_ONLY_MESSAGE, WalletActionError } from './errors';
import { releasePreparedWallet, type PreparedWallet } from './prepare';
import { toWalletActionError } from './wrap';

export interface TypedDataField {
  readonly name: string;
  readonly type: string;
}

/** An eth_signTypedData_v4 payload. `types` includes EIP712Domain. */
export interface TypedDataPayload {
  readonly types: Readonly<Record<string, readonly TypedDataField[]>>;
  readonly primaryType: string;
  readonly domain: ethers.TypedDataDomain;
  readonly message: Readonly<Record<string, unknown>>;
}

export const EIP712_DOMAIN_WITH_CHAIN: readonly TypedDataField[] = [
  { name: 'name', type: 'string' },
  { name: 'version', type: 'string' },
  { name: 'chainId', type: 'uint256' },
  { name: 'verifyingContract', type: 'address' },
];

export const EIP712_DOMAIN_NAME_VERSION: readonly TypedDataField[] = [
  { name: 'name', type: 'string' },
  { name: 'version', type: 'string' },
];

/**
 * A payload encoded by ethers (uint256 values as decimal strings, EIP712Domain derived from the
 * domain), with a numeric domain chainId: some wallets refuse ethers' hex form (b3457947).
 */
export function typedDataFromEthers(
  domain: ethers.TypedDataDomain,
  types: Record<string, ethers.TypedDataField[]>,
  message: Record<string, unknown>
): TypedDataPayload {
  const payload = ethers.TypedDataEncoder.getPayload(domain, types, message);
  if (domain.chainId != null) {
    payload.domain.chainId = ethers.getNumber(domain.chainId);
  }
  return {
    types: payload.types,
    primaryType: payload.primaryType,
    domain: payload.domain,
    message: payload.message,
  };
}

function boundChainId(domain: ethers.TypedDataDomain): number | null {
  return domain.chainId === undefined || domain.chainId === null ? null : Number(domain.chainId);
}

function assertPayloadMatchesAction(p: PreparedWallet, typedData: TypedDataPayload): void {
  const bound = boundChainId(typedData.domain);
  const required = p.action.chainId;
  const matches =
    p.action.kind === 'chainless-signature' ? bound === null : bound === null || bound === required;
  if (matches) return;
  const name = (chainId: number | null) =>
    chainId === null ? 'no network' : getChainDisplayName(chainId);
  throw new WalletActionError({
    message: `This signature is for ${name(bound)}, but the action needs ${name(required)}.`,
    phase: 'sign',
    category: 'chain_error',
    walletKind: p.walletKind,
    requiredChainId: required,
    cause: null,
  });
}

const bigintAsDecimal = (_key: string, value: unknown): unknown =>
  typeof value === 'bigint' ? value.toString() : value;

export async function signTypedData(
  p: PreparedWallet,
  typedData: TypedDataPayload
): Promise<string> {
  try {
    assertPayloadMatchesAction(p, typedData);
    const json = JSON.stringify(
      {
        types: typedData.types,
        primaryType: typedData.primaryType,
        domain: typedData.domain,
        message: typedData.message,
      },
      bigintAsDecimal
    );
    return (await p.provider.request({
      method: 'eth_signTypedData_v4',
      params: [p.address, json],
    })) as string;
  } catch (error) {
    releasePreparedWallet(p);
    throw toWalletActionError(error, p, 'sign');
  }
}

/** Raw secp256k1 over a digest (no EIP-191 prefix). Only the Privy embedded wallet supports it. */
export async function signRaw(p: PreparedWallet, digest: `0x${string}`): Promise<string> {
  try {
    if (p.walletKind !== 'embedded') {
      throw new WalletActionError({
        message: PHENOM_WALLET_ONLY_MESSAGE,
        phase: 'sign',
        category: 'unsupported-account',
        walletKind: p.walletKind,
        requiredChainId: p.action.chainId,
        cause: null,
      });
    }
    return (await p.provider.request({ method: 'secp256k1_sign', params: [digest] })) as string;
  } catch (error) {
    releasePreparedWallet(p);
    throw toWalletActionError(error, p, 'sign');
  }
}

/** Narrows a computed hash to the digest type signRaw takes. */
export function toDigest(value: string): `0x${string}` {
  if (!ethers.isHexString(value, 32)) {
    throw new Error(`Expected a 32-byte digest, got ${value}`);
  }
  return value as `0x${string}`;
}
